/**
 * Relay WebSocket client (`wss://api.<domain>/relay`).
 *
 * - first frame `{t:'auth', jwt, role, last_seq}` on open, within TIMINGS.relayAuthDeadlineMs
 * - `seq` is advanced only on frames that carry one (control frames carry none)
 * - heartbeat every 15 s with sip / audio_ok / visibility / locked / idle / lines_up
 * - every command carries a `cid`; the promise resolves on the matching `ack`, rejects after
 *   `ackTimeoutMs` (the UI shows a spinner meanwhile and a toast on failure); commands still in
 *   flight when the socket drops are re-sent with the same cid after re-auth so the relay's
 *   300 s ack cache answers them
 * - backoff 1, 2, 4, 8 then 15 s capped, ±30 % jitter, unlimited attempts
 */
import { Heartbeat, RelayEvent, TIMINGS, type RelayCommand, type SessionRole } from '@ccphone/contracts';
import type { Logger } from '../log.js';
import { type TimerHost, realTimers } from '../keepalive/timers.js';
import { backoffDelayMs } from './backoff.js';

export type AckEvent = Extract<RelayEvent, { t: 'ack' }>;
export type RelayStatus = 'idle' | 'connecting' | 'authenticating' | 'open' | 'reconnecting' | 'stopped';
export type HeartbeatBody = Omit<Heartbeat, 't'>;
export type CommandInput = RelayCommand extends infer C ? (C extends { cid: string } ? Omit<C, 'cid'> : never) : never;

export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code?: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export const WS_OPEN = 1;

export interface RelayClientOptions {
  url: string;
  role: SessionRole;
  getJwt(): string;
  heartbeat(): HeartbeatBody;
  onEvent(ev: RelayEvent): void;
  onStatus?(status: RelayStatus, attempt: number): void;
  log: Logger;
  wsFactory?: (url: string) => WebSocketLike;
  timers?: TimerHost;
  random?: () => number;
  newCid?: () => string;
  heartbeatIntervalMs?: number;
  ackTimeoutMs?: number;
  authTimeoutMs?: number;
  /** docks send no heartbeats */
  sendHeartbeats?: boolean;
}

export class RelayCommandError extends Error {
  constructor(
    readonly kind: 'timeout' | 'nack' | 'closed',
    readonly cid: string,
    readonly code?: string,
  ) {
    super(kind === 'timeout' ? 'no response from the phone server' : kind === 'nack' ? code ?? 'rejected' : 'relay closed');
    this.name = 'RelayCommandError';
  }
}

interface Pending {
  cmd: RelayCommand;
  resolve: (a: AckEvent) => void;
  reject: (e: RelayCommandError) => void;
  timer: unknown;
  sentAt: number;
}

export const ACK_TIMEOUT_MS = 2000;

export class RelayClient {
  private ws: WebSocketLike | null = null;
  private status: RelayStatus = 'idle';
  private stopped = true;
  private authed = false;
  private attempt = 0;
  private lastSeq: string | null = null;
  private hbHandle: unknown = null;
  private authTimer: unknown = null;
  private reconnectTimer: unknown = null;
  private readonly pending = new Map<string, Pending>();
  private readonly timers: TimerHost;
  private readonly random: () => number;
  private readonly newCid: () => string;
  private readonly hbMs: number;
  private readonly ackMs: number;
  private readonly authMs: number;
  private readonly wsFactory: (url: string) => WebSocketLike;
  private gen = 0;

  constructor(private readonly o: RelayClientOptions) {
    this.timers = o.timers ?? realTimers;
    this.random = o.random ?? Math.random;
    this.newCid = o.newCid ?? (() => crypto.randomUUID());
    this.hbMs = o.heartbeatIntervalMs ?? TIMINGS.heartbeatIntervalMs;
    this.ackMs = o.ackTimeoutMs ?? ACK_TIMEOUT_MS;
    this.authMs = o.authTimeoutMs ?? TIMINGS.relayAuthDeadlineMs;
    this.wsFactory = o.wsFactory ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
  }

  get currentStatus(): RelayStatus {
    return this.status;
  }

  /** open and authenticated */
  get healthy(): boolean {
    return this.status === 'open' && this.authed;
  }

  get lastSequence(): string | null {
    return this.lastSeq;
  }

  get reconnectAttempt(): number {
    return this.attempt;
  }

  get inflight(): number {
    return this.pending.size;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.connect();
  }

  stop(code = 1000): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    this.gen += 1;
    if (ws) {
      try {
        ws.close(code, 'stopped');
      } catch {
        /* ignore */
      }
    }
    this.authed = false;
    for (const [cid, p] of this.pending) {
      this.timers.clearTimeout(p.timer);
      p.reject(new RelayCommandError('closed', cid));
    }
    this.pending.clear();
    this.setStatus('stopped');
  }

  /** reconnect immediately (sleep/resume, IP change, Retry now) */
  kick(): void {
    if (this.stopped) return;
    if (this.reconnectTimer !== null) {
      this.timers.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws && this.status === 'open') return;
    this.dropSocket();
    this.connect();
  }

  /** send a heartbeat now (visibility change, lock, after reconnect) */
  heartbeatNow(): void {
    if (!this.healthy || this.o.sendHeartbeats === false) return;
    const body = Heartbeat.parse({ t: 'hb', ...this.o.heartbeat() });
    this.rawSend(body);
  }

  send(input: CommandInput): Promise<AckEvent> {
    const cid = this.newCid();
    const cmd = { ...input, cid } as RelayCommand;
    return new Promise<AckEvent>((resolve, reject) => {
      const timer = this.timers.setTimeout(() => {
        if (this.pending.delete(cid)) reject(new RelayCommandError('timeout', cid));
      }, this.ackMs);
      this.pending.set(cid, { cmd, resolve, reject, timer, sentAt: Date.now() });
      if (this.healthy) this.rawSend(cmd);
      // else: queued; sent after the next successful auth (cached ack rule)
    });
  }

  private setStatus(s: RelayStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.o.onStatus?.(s, this.attempt);
  }

  private clearTimers(): void {
    if (this.hbHandle !== null) this.timers.clearInterval(this.hbHandle);
    if (this.authTimer !== null) this.timers.clearTimeout(this.authTimer);
    if (this.reconnectTimer !== null) this.timers.clearTimeout(this.reconnectTimer);
    this.hbHandle = null;
    this.authTimer = null;
    this.reconnectTimer = null;
  }

  private dropSocket(): void {
    const ws = this.ws;
    this.ws = null;
    this.authed = false;
    this.gen += 1;
    if (this.hbHandle !== null) this.timers.clearInterval(this.hbHandle);
    if (this.authTimer !== null) this.timers.clearTimeout(this.authTimer);
    this.hbHandle = null;
    this.authTimer = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close(1000, 'reconnect');
      } catch {
        /* ignore */
      }
    }
  }

  private connect(): void {
    if (this.stopped) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const gen = ++this.gen;
    let ws: WebSocketLike;
    try {
      ws = this.wsFactory(this.o.url);
    } catch (e) {
      this.o.log.warn({ err: (e as Error).message }, 'relay socket construct failed');
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    // the auth frame must be acknowledged (any frame back) within the deadline
    this.authTimer = this.timers.setTimeout(() => {
      if (gen !== this.gen) return;
      this.o.log.warn({ deadline_ms: this.authMs }, 'relay auth deadline passed');
      this.onSocketClosed(gen, 4000, 'auth timeout');
    }, this.authMs);
    ws.onopen = () => {
      if (gen !== this.gen) return;
      this.setStatus('authenticating');
      this.rawSend({ t: 'auth', jwt: this.o.getJwt(), role: this.o.role, last_seq: this.lastSeq });
    };
    ws.onmessage = (ev) => {
      if (gen !== this.gen) return;
      this.onMessage(ev.data);
    };
    ws.onclose = (ev) => {
      if (gen !== this.gen) return;
      this.onSocketClosed(gen, ev.code, ev.reason);
    };
    ws.onerror = () => {
      /* close follows */
    };
  }

  private onSocketClosed(gen: number, code?: number, reason?: string): void {
    if (gen !== this.gen) return;
    this.o.log.info({ code, reason, authed: this.authed }, 'relay socket closed');
    this.dropSocket();
    if (this.stopped) return;
    // 4409 replaced / 4401 revoked: the server told us not to come back; the event reducer handles the UX
    if (code === 4409 || code === 4401) {
      this.stopped = true;
      this.setStatus('stopped');
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    const delay = backoffDelayMs(this.attempt, this.random);
    this.attempt += 1;
    this.setStatus('reconnecting');
    this.o.log.info({ attempt: this.attempt, delay_ms: delay }, 'relay reconnect scheduled');
    this.reconnectTimer = this.timers.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private onAuthenticated(): void {
    this.authed = true;
    this.attempt = 0;
    if (this.authTimer !== null) this.timers.clearTimeout(this.authTimer);
    this.authTimer = null;
    this.setStatus('open');
    if (this.o.sendHeartbeats !== false) {
      this.heartbeatNow();
      this.hbHandle = this.timers.setInterval(() => this.heartbeatNow(), this.hbMs);
    }
    // re-send in-flight commands with their original cid: the relay answers from its ack cache
    for (const p of this.pending.values()) this.rawSend(p.cmd);
  }

  private onMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    let json: unknown;
    try {
      json = JSON.parse(data);
    } catch {
      this.o.log.warn('relay frame is not JSON');
      return;
    }
    const parsed = RelayEvent.safeParse(json);
    if (!parsed.success) {
      this.o.log.warn({ issue: parsed.error.issues[0]?.message, t: (json as { t?: unknown })?.t }, 'relay frame rejected by schema');
      return;
    }
    const ev = parsed.data;
    if (!this.authed) this.onAuthenticated();
    if (ev.seq !== undefined) this.lastSeq = ev.seq;
    if (ev.t === 'ack') {
      const p = this.pending.get(ev.cid);
      if (p) {
        this.pending.delete(ev.cid);
        this.timers.clearTimeout(p.timer);
        if (ev.ok) p.resolve(ev);
        else p.reject(new RelayCommandError('nack', ev.cid, ev.error));
      }
    }
    try {
      this.o.onEvent(ev);
    } catch (e) {
      this.o.log.error({ err: (e as Error).message, t: ev.t }, 'event handler threw');
    }
  }

  private rawSend(frame: unknown): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WS_OPEN) return;
    try {
      ws.send(JSON.stringify(frame));
    } catch (e) {
      this.o.log.warn({ err: (e as Error).message }, 'relay send failed');
    }
  }
}
